import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { AvatarChip } from '@/components/AvatarChip';
import { container } from '@/services/container';
import { useNetworkStore } from '@/store/network';
import { useFeedStore } from '@/store/feed';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { unseenCircleCount } from '@/features/circle/circleSeen';
import { MeshLinkError } from '@/lib/result';
import { fetchMerchantProfiles, getMyLocation, haversineKm, type MerchantProfile } from '@/lib/merchantApi';
import {
  OFFICIAL_ADDRESS,
  OFFICIAL_NAME,
  OFFICIAL_ANNOUNCEMENTS,
  markOfficialFollowed,
} from '@/utils/official';

/** 商家聚合：v3.34 从链上 promo 活动现算（发现页商家运营入口）。 */
interface PromoRowV {
  promo_id: string;
  from: string;
  budget: number;
  remaining: number;
  coupon_value: number;
  max_uses: number;
  used: number;
  expires_height: number;
}
interface MerchantBrief {
  address: string;
  activities: PromoRowV[];
  totalBudget: number;
  totalRedeemed: number;
  activeCount: number;
  /** v3.35 LBS：与我的距离（公里）；未定位或无经纬度时为 undefined。 */
  distKm?: number;
}
const dpbVal = (v: number | undefined): string =>
  Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 2 });

/** did:dpb:0x… → 0x… 地址（创作者关注/订阅的目标）。 */
function addressFromDid(did: string): string {
  const s = String(did ?? '').trim();
  return s.startsWith('did:dpb:') ? s.slice('did:dpb:'.length).toLowerCase() : '';
}

/**
 * 发现页：在线用户目录 + 按 PeerID 添加好友 + 创作者（关注/订阅挖矿）。
 *
 * 在线列表由 `RelayService` 心跳（15s）通过 `useNetworkStore.online` 实时更新；
 * 「刷新在线」按钮先清空列表、3s 后回填刷新前的快照 —— 给用户一个可观测的刷新过程，
 * 不改变心跳维护的真实数据。
 */
export function DiscoverPage(): JSX.Element {
  const navigate = useNavigate();
  const online = useNetworkStore((s) => s.online);
  const lastRefresh = useNetworkStore((s) => s.lastOnlineRefreshAt);
  const relayStatus = useNetworkStore((s) => s.relay.status);
  const posts = useFeedStore((s) => s.posts);
  const contacts = useChatStore((s) => s.contacts);
  const following = useSocialStore((s) => s.following);
  const selfDid = useIdentityStore((s) => s.did);

  const [peerIdInput, setPeerIdInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  // v3.34 商家聚合（链上 promo 活动）+ v3.35 LBS 附近排序
  const [merchants, setMerchants] = useState<MerchantBrief[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [nearbyMode, setNearbyMode] = useState(false);
  const [myLoc, setMyLoc] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [profileMap, setProfileMap] = useState<Record<string, MerchantProfile>>({});

  /** 拉链上全部 promo 活动 → 按商家聚合；若已定位则按距离排序（附近商家）。 */
  const loadMerchants = useMemo(
    () => async (): Promise<void> => {
      setLoadingMerchants(true);
      try {
        const [pl, h, profiles] = await Promise.all([
          container.chain.promoList().catch(() => ({ items: [] }) as Record<string, unknown>),
          container.chain.getBlockHeight().catch(() => 0),
          fetchMerchantProfiles().catch(() => [] as MerchantProfile[]),
        ]);
        const pm: Record<string, MerchantProfile> = {};
        for (const p of profiles) pm[p.address.toLowerCase()] = p;
        setProfileMap(pm);
        const items = (pl?.items ?? []) as PromoRowV[];
        const byAddr = new Map<string, MerchantBrief>();
        for (const it of items) {
          const from = String(it.from ?? '').toLowerCase();
          if (!from) continue;
          const m = byAddr.get(from) ?? {
            address: from,
            activities: [],
            totalBudget: 0,
            totalRedeemed: 0,
            activeCount: 0,
          };
          m.activities.push(it);
          m.totalBudget += Number(it.budget ?? 0);
          if (h > 0 && Number(it.expires_height ?? 0) >= h && Number(it.used ?? 0) < Number(it.max_uses ?? 0)) {
            m.activeCount += 1;
          }
          byAddr.set(from, m);
        }
        const loc = await getMyLocation();
        setMyLoc(loc);
        const list = [...byAddr.values()];
        if (loc) {
          // 附近模式：档案有经纬度的商家按距离排序，无经纬度的排后
          const withDist = list.map((m) => {
            const p = pm[m.address];
            if (p && typeof p.lat === 'number' && typeof p.lng === 'number') {
              return { ...m, distKm: haversineKm(loc.lat, loc.lng, p.lat, p.lng) };
            }
            return { ...m, distKm: undefined };
          });
          withDist.sort(
            (a, b) =>
              (a.activeCount - b.activeCount) * -1 ||
              (a.distKm ?? Number.MAX_SAFE_INTEGER) - (b.distKm ?? Number.MAX_SAFE_INTEGER),
          );
          setMerchants(withDist.slice(0, 20));
          setNearbyMode(true);
        } else {
          list.sort((a, b) => b.activeCount - a.activeCount || b.totalBudget - a.totalBudget);
          setMerchants(list.slice(0, 20));
          setNearbyMode(false);
        }
      } catch {
        setMerchants([]);
        setNearbyMode(false);
      } finally {
        setLoadingMerchants(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadMerchants();
  }, [loadMerchants]);

  const copyPromo = async (pid: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(pid);
      useUiStore.getState().pushToast('success', '优惠券核销码已复制（发给好友核销即分账）');
    } catch {
      useUiStore.getState().pushToast('error', '复制失败，请手动复制');
    }
  };

  // 创作者聚合：从广场帖作者去重，提取链地址（关注/订阅目标）
  const creators = useMemo(() => {
    const map = new Map<string, { did: string; address: string; name: string; posts: number }>();
    for (const p of posts) {
      const did = typeof p.authorDid === 'string' ? p.authorDid : '';
      const address = addressFromDid(did);
      if (!address) continue;
      const prev = map.get(address);
      const name = typeof p.authorName === 'string' && p.authorName ? p.authorName : address.slice(0, 10);
      if (prev) {
        prev.posts += 1;
      } else {
        map.set(address, { did, address, name, posts: 1 });
      }
    }
    return [...map.values()].sort((a, b) => b.posts - a.posts).slice(0, 20);
  }, [posts]);

  const doAdd = async (id: string): Promise<void> => {
    const trimmed = id.trim();
    if (!trimmed) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setAdding(true);
    try {
      await container.chat.addContact(trimmed, '');
      setPeerIdInput('');
      useUiStore.getState().pushToast('success', '已发送好友请求');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setAdding(false);
    }
  };

  /** 朋友圈未读（v3.21）：好友动态数中晚于已读时间戳的数量。 */
  const circleUnseen = useMemo(() => {
    const set = new Set<string>();
    for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
    for (const d of following) set.add(d.toLowerCase());
    if (selfDid) set.add(selfDid.toLowerCase());
    return unseenCircleCount(posts, set);
  }, [posts, contacts, following, selfDid]);

  const refresh = (): void => {
    if (refreshing) return;
    setRefreshing(true);
    // 先清空 online（列表位置会短暂显示 EmptyState）→ 3s 后把刷新前快照重新推回，
    // 用可观测的变化模拟「重新拉取在线列表」的过程；真实列表仍由 15s 心跳维护。
    const snapshot = useNetworkStore.getState().online;
    useNetworkStore.getState().setOnline([]);
    window.setTimeout(() => {
      useNetworkStore.getState().setOnline(snapshot);
      setRefreshing(false);
    }, 3_000);
  };

  /** 关注 / 订阅创作者（社交动作上链 → 行为挖矿奖励）。 */
  const engage = async (action: 'follow' | 'subscribe', address: string, name: string): Promise<void> => {
    const key = `${action}:${address}`;
    if (busyAction) return;
    const self = container.identity.address;
    if (!self) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setBusyAction(key);
    try {
      await container.chain.socialAct({
        action,
        from: self as `0x${string}`,
        target: address as `0x${string}`,
        signature: container.identity.signObject({ action, from: self, target: address, ts: Date.now() }),
      });
      if (address === OFFICIAL_ADDRESS) markOfficialFollowed();
      useUiStore.getState().pushToast('success', action === 'follow' ? `已关注 ${name}` : `已订阅 ${name}（上链）`);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* 朋友圈（v3.20）：好友动态流入口 */}
      <section className="card p-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-xl" aria-hidden>
            📸
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text">朋友圈</p>
            <p className="mt-0.5 text-[11px] text-dim">仅好友与关注对象的动态 · 分享此刻（发文即挖矿）</p>
          </div>
          <Button size="sm" onClick={() => navigate('/circle')}>
            去看看{circleUnseen > 0 ? ` · ${circleUnseen}` : ''}
          </Button>
        </div>
      </section>

      {/* 官方号（v3.16 内容冷启动）：官方公告 + 一键关注（上链 + 联动新手任务） */}
      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">📢 {OFFICIAL_NAME}</h2>
          <Button
            variant="ghost"
            size="sm"
            loading={busyAction === `follow:${OFFICIAL_ADDRESS}`}
            onClick={() => void engage('follow', OFFICIAL_ADDRESS, OFFICIAL_NAME)}
          >
            关注官方号（上链）
          </Button>
        </div>
        <ul className="space-y-2.5">
          {OFFICIAL_ANNOUNCEMENTS.map((a) => (
            <li key={a.title} className="rounded-btn bg-panel-2 px-3 py-2.5">
              <p className="text-xs font-medium text-text">
                {a.icon} {a.title}
                <span className="ml-2 text-[11px] text-dim">{a.ts}</span>
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-dim">{a.body}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* v3.34 商家运营：链上推广活动聚合（消费者领券核销入口） */}
      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">
            🏪 {nearbyMode ? '附近商家' : '商家优惠'}{' '}
            <span className="text-[11px] text-dim">
              ({nearbyMode ? '按距离排序' : '推广活动核销'} · {merchants.length})
            </span>
          </h2>
          <Button variant="ghost" size="sm" loading={loadingMerchants} onClick={() => void loadMerchants()}>
            刷新
          </Button>
        </div>
        {nearbyMode && myLoc ? (
          <p className="mb-3 text-[11px] text-dim">
            📍 已定位（{myLoc.lat.toFixed(3)}, {myLoc.lng.toFixed(3)}），商家按距离由近到远展示；
            未设置位置的商家排在末尾。关闭定位权限则回退热门排序。
          </p>
        ) : null}
        {merchants.length === 0 ? (
          <EmptyState title={nearbyMode ? '附近暂无商家' : '暂无商家活动'} description="商家创建推广活动并设置店铺位置后，将在此按距离展示（消费即核销分账）" />
        ) : (
          <ul className="space-y-2">
            {merchants.map((m) => {
              const profile = profileMap[m.address];
              const name = profile?.name ?? `商家 ${m.address.slice(0, 6)}…${m.address.slice(-4)}`;
              const cat = profile?.category ? ` · ${profile.category}` : '';
              const city = profile?.city ? ` · ${profile.city}` : '';
              const distText =
                m.distKm !== undefined
                  ? m.distKm < 1
                    ? `${Math.round(m.distKm * 1000)}m`
                    : `${m.distKm.toFixed(1)}km`
                  : undefined;
              return (
              <li key={m.address} className="rounded-btn bg-panel-2 px-3 py-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-text">
                      {name}{cat}{city}
                      {m.activeCount > 0 ? <span className="ml-2 text-[10px] text-ok">进行中 {m.activeCount}</span> : null}
                      {distText ? <span className="ml-2 text-[10px] text-accent">📍 {distText}</span> : null}
                    </p>
                    <p className="mt-0.5 text-[11px] text-dim">
                      <span className="mono">{m.address.slice(0, 10)}…{m.address.slice(-6)}</span>
                      {profile?.addr ? ` · ${profile.addr}` : ''}
                    </p>
                  </div>
                </div>
                <div className="mt-2 space-y-1.5">
                  {m.activities.slice(0, 3).map((a) => {
                    const alive =
                      Number(a.expires_height ?? 0) >= 0 && Number(a.used ?? 0) < Number(a.max_uses ?? 0);
                    return (
                      <div key={a.promo_id} className="flex flex-wrap items-center justify-between gap-2 rounded-btn bg-panel-2/60 px-2 py-1.5 text-[11px]">
                        <span className="font-mono text-text">券值 {dpbVal(a.coupon_value)} DPB</span>
                        <span className="text-dim">核销 {a.used}/{a.max_uses}</span>
                        <span className="text-dim">剩余 {dpbVal(a.remaining)}</span>
                        {alive ? (
                          <Button variant="outline" size="sm" onClick={() => void copyPromo(a.promo_id)}>
                            复制核销码
                          </Button>
                        ) : (
                          <span className="text-[10px] text-dim">已结束</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">按 PeerID 添加好友</h2>
        <div className="flex gap-2">
          <Input
            value={peerIdInput}
            onChange={(e) => setPeerIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doAdd(peerIdInput);
            }}
            placeholder="粘贴对方 PeerID"
          />
          <Button size="sm" loading={adding} onClick={() => void doAdd(peerIdInput)}>
            添加
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">
            在线用户 <span className="text-[11px] text-dim">({online.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            {refreshing ? <span className="text-[11px] text-ok">刷新中…</span> : null}
            <Button variant="ghost" size="sm" loading={refreshing} onClick={refresh}>
              刷新在线
            </Button>
          </div>
        </div>

        <p className="mb-3 text-[11px] text-dim">
          中继：{relayStatus}
          {lastRefresh ? ` · 上次刷新 ${new Date(lastRefresh).toLocaleTimeString()}` : ''}
        </p>

        {online.length === 0 ? (
          <EmptyState title="附近暂无在线用户" description="稍等片刻或邀请好友上线" />
        ) : (
          <ul className="space-y-2">
            {online.map((p) => (
              <li
                key={p.peerId}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate text-text">{p.name || '匿名用户'}</p>
                  <p className="mono truncate text-[11px] text-dim">{p.peerId}</p>
                </div>
                <Button variant="ghost" size="sm" loading={adding} onClick={() => void doAdd(p.peerId)}>
                  加好友
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">
          创作者 <span className="text-[11px] text-dim">(关注/订阅即挖矿 · {creators.length})</span>
        </h2>
        {creators.length === 0 ? (
          <EmptyState title="暂无创作者" description="广场帖子会聚合出创作者列表" />
        ) : (
          <ul className="space-y-2">
            {creators.map((c) => (
              <li
                key={c.address}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate text-text">{c.name}</p>
                  <p className="mono truncate text-[11px] text-dim">
                    {c.address.slice(0, 10)}… · {c.posts} 帖
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <AvatarChip did={`did:dpb:${c.address}`} name={c.name} size="sm" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/u/${encodeURIComponent(`did:dpb:${c.address}`)}`)}
                  >
                    主页
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busyAction === `follow:${c.address}`}
                    onClick={() => void engage('follow', c.address, c.name)}
                  >
                    关注
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={busyAction === `subscribe:${c.address}`}
                    onClick={() => void engage('subscribe', c.address, c.name)}
                  >
                    订阅
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default DiscoverPage;
